import { useMember } from "api/members";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { handledCampaignTriggers } from "constants/general";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useNavigate } from "react-router-dom";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { USER_VISIBLE_CAMPAIGNS_LIST_ID } from "state/tables/types";
import { VisibleCampaign } from "types/campaigns";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";

export function VisibleCampaigns({ memberID }: { memberID: string }) {
  const navigate = useNavigate();
  const { message } = useMessage();
  const { listing, updateListing } = useListing(USER_VISIBLE_CAMPAIGNS_LIST_ID);
  const { setColumnOrdering } = useTablesStateHelpers();

  const { useMembersVisibleCampaigns } = useMember(memberID);
  const { data: membersCampaigns } = useMembersVisibleCampaigns({
    filters: [
      {
        condition: "neq",
        query: "trigger",
        value: "leaderboard",
      },
    ],
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "name",
  });

  const headItems = createHeadItems([
    {
      headerText: message({ id: "campaignName" }),
      isSortable: true,
      property: "name",
    },
    {
      headerText: message({ id: "status" }),
      property: "status",
      isSortable: false,
    },
    {
      align: "right",
      headerText: message({ id: "campaignType" }),
      property: "type",
    },
    {
      align: "right",
      headerText: message({ id: "campaignTrigger" }),
      property: "trigger",
    },
    {
      headerText: message({ id: "activeFrom" }),
      isSortable: true,
      property: "startsAt",
    },
    {
      headerText: message({ id: "activeTo" }),
      isSortable: true,
      property: "endsAt",
    },
    {
      headerText: message({ id: "createdOn" }),
      isSortable: true,
      property: "createdAt",
    },
  ]);

  function createItems(items: VisibleCampaign[]): Row[] {
    return items.map((item) => {
      return {
        id: item.campaignId,
        cells: [
          {
            content: item.name,
            property: "name",
          },
          {
            content: <StatusValue status={item.active} />,
            property: "status",
          },
          {
            align: "right",
            content: message({ id: item.type }),
            property: "type",
          },
          {
            align: "right",
            content: message({
              id: handledCampaignTriggers.find(
                (trigger) => trigger.id === item.trigger,
              )?.label,
            }),
            property: "trigger",
          },
          {
            content: OLDate(item.activity?.startsAt || undefined),
            property: "startsAt",
          },
          {
            content: OLDate(item.activity?.endsAt || undefined),
            property: "endsAt",
          },
          {
            content: OLDate(item.createdAt),
            property: "createdAt",
          },
        ],
        metadata: { disabled: !item.active },
      };
    });
  }

  const items = membersCampaigns?.items
    ? createItems(membersCampaigns.items)
    : [];

  return (
    <OLTable
      actions={{
        body: [
          {
            action: (
              id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
            ) =>
              navigate(links.campaign(`${id}`), {
                state: { prevPath: links.singleMemberCampaigns(memberID) },
              }),
            label: message({ id: "campaignDetails" }),
          },
        ],
      }}
      columns={membersCampaigns && headItems}
      data-test-id="single-member-visible-campaigns"
      onSort={(prop) =>
        updateListing({
          payload: prop,
          type: ListingActions.SET_SORT,
        })
      }
      onRowClick={(id: string) => {
        navigate(links.campaign(id as string), {
          state: { prevPath: links.singleMemberCampaigns(memberID) },
        });
      }}
      pagination={{
        count: calculateCount(
          membersCampaigns?.total.filtered,
          membersCampaigns?.total.all,
        ),
        onChangePage: (page) =>
          updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
        onChangeRowsPerPage: (rows) =>
          updateListing({
            payload: rows,
            type: ListingActions.CHANGE_ROWS_PER_PAGE,
          }),
        page: listing.page,
        rowsPerPage: listing.rowsPerPage,
      }}
      rows={membersCampaigns && items}
      sort={{
        order: listing.order || "asc",
        property: listing.property || "name",
        rowsPerPage: listing.rowsPerPage,
        page: listing.page,
      }}
      setColumnOrdering={setColumnOrdering}
      tableName={USER_VISIBLE_CAMPAIGNS_LIST_ID}
    />
  );
}
